// Budgets — redesigned 2026-04-21, v2 2026-04-21:
// Split mode into stateFilter + selectedId so clicking a project in the
// sidebar doesn't reset the state filter that was narrowing the sidebar.
//
// Layout:
//   - Top: state summary strip (All / WY / CO / NE) with totals
//   - Left: searchable sidebar grouped by state, filtered by stateFilter
//   - Main: either summary table (for stateFilter's scope) or selected
//     project's budget categories
//   - Right rail: totals + federal/match donut for whichever view is active
//
// Same API (/bead/projects, /bead/projects/:id/budget).
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import BudgetSetupWizard from '../components/BudgetSetupWizard';
import ImportFromReadyModal from '../components/ImportFromReadyModal';
import NewProjectWizard from '../components/NewProjectWizard';

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county: string;
  tech_type: string;
  awarded_bsl_count: number;
  location_count: number;
  budget_line_count: number;
  total_budget_usd: string | null;
  state: string;
};

type BudgetLine = {
  id: string;
  category: string;
  subcategory: string | null;
  line_item: string;
  description: string | null;
  unit: string | null;
  quantity: string | null;
  unit_cost_usd: string | null;
  total_cost_usd: string | null;
  federal_share_usd: string | null;
  match_share_usd: string | null;
};

const fmtUSD = (n: string | number | null | undefined) => {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};
const fmtNum = (n: any) => {
  const v = Number(n);
  return isFinite(v) ? Math.round(v).toLocaleString() : '—';
};

type StateFilter = 'all' | string;

export default function BudgetsPage() {
  const [imp, setImp] = useState(false);
  const [newProj, setNewProj] = useState(false);
  const [budgetForNew, setBudgetForNew] = useState<any>(null);
  // stateFilter = 'all' | 'WY' | 'CO' | 'NE' | ...
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  // Optional project drilldown — independent of stateFilter so clicking a
  // project doesn't collapse the state grouping in the sidebar.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: 'total' | 'bsls' | 'name' | 'state'; dir: 'asc' | 'desc' }>({ key: 'total', dir: 'desc' });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });

  const { data: budget } = useQuery<{ lines: BudgetLine[]; totals: { total: string; federal: string; match: string } }>({
    queryKey: ['bead-budget', selectedId],
    queryFn: () => selectedId ? api.get(`/bead/projects/${selectedId}/budget`) : Promise.reject(),
    enabled: !!selectedId,
  });

  // --- rollups --------------------------------------------------------
  const stateRollup = useMemo(() => {
    const out: Record<string, { projects: number; bsls: number; total: number }> = {};
    for (const p of projects) {
      const s = p.state || '??';
      if (!out[s]) out[s] = { projects: 0, bsls: 0, total: 0 };
      out[s].projects += 1;
      out[s].bsls += Number(p.awarded_bsl_count || 0);
      out[s].total += Number(p.total_budget_usd || 0);
    }
    return out;
  }, [projects]);
  const grand = useMemo(() => {
    const totals = { projects: projects.length, bsls: 0, total: 0 };
    for (const s of Object.values(stateRollup)) { totals.bsls += s.bsls; totals.total += s.total; }
    return totals;
  }, [projects, stateRollup]);

  // --- sidebar filter -------------------------------------------------
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    const inState = (p: Project) => stateFilter === 'all' ? true : p.state === stateFilter;
    return projects
      .filter(inState)
      .filter((p) => {
        if (!q) return true;
        return (
          p.project_code.toLowerCase().includes(q) ||
          p.project_name.toLowerCase().includes(q) ||
          (p.county || '').toLowerCase().includes(q) ||
          (p.state || '').toLowerCase().includes(q)
        );
      });
  }, [projects, stateFilter, q]);

  const grouped = useMemo(() => {
    const byState: Record<string, Project[]> = {};
    for (const p of visible) {
      const s = p.state || '??';
      if (!byState[s]) byState[s] = [];
      byState[s].push(p);
    }
    for (const s of Object.keys(byState)) {
      byState[s].sort((a, b) => Number(b.total_budget_usd || 0) - Number(a.total_budget_usd || 0));
    }
    return byState;
  }, [visible]);

  // Summary rows use stateFilter too — so State-mode summaries only show
  // that state's projects.
  const summaryRows = useMemo(() => {
    const rows = stateFilter === 'all' ? projects : projects.filter((p) => p.state === stateFilter);
    const sorted = [...rows].sort((a, b) => {
      const d = sort.dir === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'total': return d * (Number(a.total_budget_usd || 0) - Number(b.total_budget_usd || 0));
        case 'bsls':  return d * ((a.awarded_bsl_count || 0) - (b.awarded_bsl_count || 0));
        case 'state': return d * ((a.state || '').localeCompare(b.state || ''));
        case 'name':  return d * ((a.project_name || '').localeCompare(b.project_name || ''));
      }
    });
    return sorted;
  }, [projects, stateFilter, sort]);

  const active = selectedId ? projects.find((p) => p.id === selectedId) : undefined;

  // When the user switches state filter, clear a stale project selection
  // if the selected project isn't part of the new state scope.
  if (active && stateFilter !== 'all' && active.state !== stateFilter) {
    // This is a render-time invalidation (setState inside render is OK
    // in React when guarded — we run once per state-filter change).
    setSelectedId(null);
  }

  // Right-rail donut (project scope only — aggregate federal/match isn't
  // on the /bead/projects summary rows).
  const donut = useMemo(() => {
    if (selectedId && budget?.totals) {
      return { federal: Number(budget.totals.federal || 0), match: Number(budget.totals.match || 0) };
    }
    return null;
  }, [selectedId, budget]);

  return (
    <div style={{ padding: 20, fontSize: 13 }}>
      {imp && <ImportFromReadyModal onClose={() => setImp(false)} />}
      {newProj && <NewProjectWizard onClose={() => setNewProj(false)} onSetupBudget={(p) => { setNewProj(false); setBudgetForNew(p); }} />}
      {budgetForNew && <BudgetSetupWizard projectId={budgetForNew.id} project={budgetForNew} onClose={() => setBudgetForNew(null)} />}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><h1 style={{ fontSize: 20, margin: 0 }}>Budgets</h1><button onClick={() => setNewProj(true)} style={{ padding: '5px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>+ New project</button><button onClick={() => setImp(true)} style={{ padding: '5px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>+ Import from Ready</button></div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {grand.projects} projects · {fmtNum(grand.bsls)} BSLs · {fmtUSD(grand.total)} total
        </div>
      </div>

      {/* Top strip: state chips. Clicking a state chip also clears any
          selected project because we've now changed the context. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Chip active={stateFilter === 'all'}
          onClick={() => { setStateFilter('all'); setSelectedId(null); }}
          primary={`All projects`} secondary={`${grand.projects} · ${fmtUSD(grand.total)}`} />
        {Object.keys(stateRollup).sort().map((s) => (
          <Chip key={s} active={stateFilter === s}
            onClick={() => { setStateFilter(s); setSelectedId(null); }}
            primary={STATE_NAME[s] || s}
            secondary={`${stateRollup[s].projects} · ${fmtUSD(stateRollup[s].total)}`} />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr 280px', gap: 16 }}>
        {/* ---------- Sidebar ---------- */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={stateFilter === 'all' ? 'Search projects…' : `Search ${STATE_NAME[stateFilter] || stateFilter}…`}
              style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, outline: 'none' }}
            />
          </div>
          {Object.keys(grouped).sort().map((s) => (
            <div key={s}>
              <div style={{ padding: '8px 10px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#0f172a', background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                {STATE_NAME[s] || s} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {grouped[s].length} · {fmtUSD(grouped[s].reduce((a, b) => a + Number(b.total_budget_usd || 0), 0))}</span>
              </div>
              {grouped[s].map((p) => {
                const isActive = selectedId === p.id;
                return (
                  <button key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '6px 10px', background: isActive ? '#ede9fe' : 'transparent',
                      border: 'none', borderLeft: isActive ? '3px solid #8b5cf6' : '3px solid transparent',
                      cursor: 'pointer', fontSize: 12,
                    }}
                    title={`${p.project_code} · ${p.county} County · ${p.awarded_bsl_count} BSLs`}
                  >
                    <div style={{ fontWeight: 600, color: '#111827' }}>{p.project_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      {p.project_code} · {p.awarded_bsl_count} BSLs · {fmtUSD(p.total_budget_usd)}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          {visible.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No projects match “{query}”</div>
          )}
        </div>

        {/* ---------- Main ---------- */}
        <div>
          {selectedId && active && budget ? (
            <ProjectDetail project={active} budget={budget} onBack={() => setSelectedId(null)} />
          ) : (
            <SummaryTable rows={summaryRows} sort={sort} setSort={setSort}
              scopeLabel={stateFilter === 'all' ? 'All projects' : (STATE_NAME[stateFilter] || stateFilter)}
              onRowClick={(id) => setSelectedId(id)} />
          )}
        </div>

        {/* ---------- Right rail ---------- */}
        <div>
          <div style={{ padding: 16, background: '#0b1220', color: '#fff', borderRadius: 8, position: 'sticky', top: 20 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', marginBottom: 4 }}>
              {selectedId ? 'Project total' : stateFilter === 'all' ? 'Total across all states' : `${STATE_NAME[stateFilter] || stateFilter} total`}
            </div>
            <div style={{ fontSize: 30, fontWeight: 700 }}>
              {fmtUSD(
                selectedId ? (budget?.totals?.total ?? active?.total_budget_usd)
                : stateFilter === 'all' ? grand.total
                : stateRollup[stateFilter]?.total
              )}
            </div>
            {donut && (donut.federal + donut.match > 0) && (
              <Donut federal={donut.federal} match={donut.match} />
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
              {selectedId ? `${active?.budget_line_count ?? 0} line items · ${active?.awarded_bsl_count ?? 0} BSLs` :
               stateFilter === 'all' ? `${grand.projects} projects · ${fmtNum(grand.bsls)} BSLs` :
               `${stateRollup[stateFilter]?.projects ?? 0} projects · ${fmtNum(stateRollup[stateFilter]?.bsls)} BSLs`}
            </div>
          </div>
        </div>
      </div>

      {projects.length === 0 && (
        <div style={{ color: 'var(--text-muted)', marginTop: 20 }}>
          No budget data ingested yet.
        </div>
      )}
    </div>
  );
}

// Full state-name map so any state the loaded projects live in renders with a
// real label (was a WY/CO/NE-only hardcode; real projects are AR/LA/MS). Unknown
// codes fall through to the 2-letter code as the label.
const STATE_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AR: 'Arkansas', AZ: 'Arizona', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DC: 'D.C.', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', IA: 'Iowa', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts',
  MD: 'Maryland', ME: 'Maine', MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri',
  MS: 'Mississippi', MT: 'Montana', NC: 'North Carolina', ND: 'North Dakota',
  NE: 'Nebraska', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NV: 'Nevada', NY: 'New York', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VA: 'Virginia', VT: 'Vermont',
  WA: 'Washington', WI: 'Wisconsin', WV: 'West Virginia', WY: 'Wyoming',
};

function Chip({ primary, secondary, active, onClick }: { primary: string; secondary?: string; active?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
      border: active ? '2px solid #8b5cf6' : '1px solid var(--border)',
      background: active ? '#ede9fe' : '#fff',
      color: active ? '#5b21b6' : '#111827',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 110,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700 }}>{primary}</span>
      {secondary && <span style={{ fontSize: 10, color: active ? '#7c3aed' : 'var(--text-muted)' }}>{secondary}</span>}
    </button>
  );
}

function Donut({ federal, match }: { federal: number; match: number }) {
  const total = federal + match;
  const fedPct = total > 0 ? (federal / total) * 100 : 0;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        width: 120, height: 120, borderRadius: '50%',
        background: `conic-gradient(#10b981 0% ${fedPct}%, #38bdf8 ${fedPct}% 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto',
      }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#0b1220', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>Federal</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{Math.round(fedPct)}%</div>
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span><Dot c="#10b981" /> Federal</span>
          <span style={{ fontWeight: 600 }}>{fmtUSD(federal)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span><Dot c="#38bdf8" /> Match</span>
          <span style={{ fontWeight: 600 }}>{fmtUSD(match)}</span>
        </div>
      </div>
    </div>
  );
}
function Dot({ c }: { c: string }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, marginRight: 6 }} />;
}

// ============================================================
// Summary table (shown in All / State modes)
// ============================================================
function SummaryTable({ rows, sort, setSort, onRowClick, scopeLabel }: {
  rows: Project[];
  sort: { key: 'total' | 'bsls' | 'name' | 'state'; dir: 'asc' | 'desc' };
  setSort: (s: any) => void;
  onRowClick: (id: string) => void;
  scopeLabel: string;
}) {
  const toggle = (k: 'total' | 'bsls' | 'name' | 'state') => {
    if (sort.key === k) setSort({ key: k, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ key: k, dir: k === 'total' || k === 'bsls' ? 'desc' : 'asc' });
  };
  const arrow = (k: string) => sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{scopeLabel} · {rows.length} project{rows.length === 1 ? '' : 's'}</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <Th onClick={() => toggle('state')}>State{arrow('state')}</Th>
              <Th onClick={() => toggle('name')}>Project{arrow('name')}</Th>
              <Th>County</Th>
              <Th>Tech</Th>
              <Th right onClick={() => toggle('bsls')}>BSLs{arrow('bsls')}</Th>
              <Th right>Lines</Th>
              <Th right onClick={() => toggle('total')}>Total budget{arrow('total')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} onClick={() => onRowClick(p.id)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                <Td><span style={{ padding: '1px 5px', background: '#e0e7ff', color: '#3730a3', fontSize: 10, fontWeight: 700, borderRadius: 3 }}>{p.state}</span></Td>
                <Td>
                  <div style={{ fontWeight: 600 }}>{p.project_name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.project_code}</div>
                </Td>
                <Td>{p.county}</Td>
                <Td style={{ color: 'var(--text-muted)' }}>{p.tech_type}</Td>
                <Td right>{fmtNum(p.awarded_bsl_count)}</Td>
                <Td right>{p.budget_line_count}</Td>
                <Td right style={{ fontWeight: 600 }}>{fmtUSD(p.total_budget_usd)}</Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No projects.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Th({ children, right, onClick }: any) {
  return <th onClick={onClick} style={{ textAlign: right ? 'right' : 'left', padding: '8px 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#111827', borderBottom: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default', userSelect: 'none' }}>{children}</th>;
}
function Td({ children, right, style }: any) {
  return <td style={{ padding: '8px 10px', textAlign: right ? 'right' : 'left', fontVariantNumeric: right ? 'tabular-nums' : undefined, ...style }}>{children}</td>;
}

// ============================================================
// Project detail
// ============================================================
function ProjectDetail({ project: p, budget, onBack }: { project: Project; budget: { lines: BudgetLine[]; totals: any }; onBack: () => void }) {
  const qc = useQueryClient();
  const [gen, setGen] = useState(false);
  const [wiz, setWiz] = useState(false);
  const generate = async () => {
    if (p.budget_line_count > 0 && !window.confirm('Regenerate the BEAD budget from the standard SF-424A template? This replaces the current budget lines.')) return;
    setGen(true);
    try {
      await api.post(`/bead/projects/${p.id}/budget/generate`, {});
      await qc.invalidateQueries({ queryKey: ['bead-budget', p.id] });
      await qc.invalidateQueries({ queryKey: ['bead-projects'] });
    } finally { setGen(false); }
  };
  const byCategory = useMemo(() => {
    const out: Record<string, BudgetLine[]> = {};
    for (const l of budget.lines) {
      const k = l.category || 'Other';
      if (!out[k]) out[k] = [];
      out[k].push(l);
    }
    return out;
  }, [budget.lines]);

  const hasGranular = useMemo(() =>
    budget.lines.some((l) => l.quantity != null || l.unit_cost_usd != null || (l.unit && l.unit.length > 0)),
    [budget.lines]);

  return (
    <div>
      {wiz && <BudgetSetupWizard projectId={p.id} project={p} onClose={() => setWiz(false)} />}
      <div style={{ marginBottom: 14, padding: 14, background: '#0b1220', color: '#fff', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 17, margin: 0 }}>{p.project_name}</h2>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {p.project_code} · {p.county} County, {p.state} · {p.tech_type} · {p.awarded_bsl_count} BSLs · {p.budget_line_count} lines
          </div>
        </div>
        <button onClick={() => setWiz(true)} style={{ padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600, marginRight: 8 }}>Import / set up budget</button>
        <button onClick={generate} disabled={gen} style={{ padding: '6px 12px', background: gen ? '#334155' : '#10b981', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: gen ? 'default' : 'pointer', fontWeight: 600, marginRight: 8 }}>{gen ? 'Generating...' : (p.budget_line_count > 0 ? 'Regenerate BEAD budget' : 'Generate BEAD budget')}</button>
        <button onClick={onBack} style={{ padding: '6px 12px', background: 'transparent', color: '#fff', border: '1px solid #334', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>← Back to list</button>
      </div>

      {Object.entries(byCategory).map(([cat, lines]) => {
        const catTotal = lines.reduce((s, l) => s + Number(l.total_cost_usd ?? 0), 0);
        const catFed = lines.reduce((s, l) => s + Number(l.federal_share_usd ?? 0), 0);
        const catMatch = lines.reduce((s, l) => s + Number(l.match_share_usd ?? 0), 0);
        const singleAndDup = lines.length === 1 && (lines[0].line_item || '').trim().toLowerCase() === cat.trim().toLowerCase();
        if (singleAndDup) {
          return (
            <div key={cat} style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 8, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, alignItems: 'center', background: '#fff' }}>
              <div style={{ fontWeight: 600 }}>{cat}</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(catTotal)}</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#10b981' }}>{fmtUSD(catFed)}</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>{fmtUSD(catMatch)}</div>
            </div>
          );
        }
        return (
          <div key={cat} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <h3 style={{ fontSize: 12, margin: 0, textTransform: 'uppercase', letterSpacing: '.06em', color: '#111827' }}>{cat}</h3>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{fmtUSD(catTotal)}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={thL}>Line item</th>
                  {hasGranular && <th style={thR}>Qty</th>}
                  {hasGranular && <th style={{ ...thL, width: 50 }}>Unit</th>}
                  {hasGranular && <th style={thR}>Unit $</th>}
                  <th style={thR}>Total</th>
                  <th style={thR}>Federal</th>
                  <th style={thR}>Match</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={td}>{l.line_item}</td>
                    {hasGranular && <td style={tdR}>{l.quantity != null ? Number(l.quantity).toLocaleString() : '—'}</td>}
                    {hasGranular && <td style={{ ...td, color: 'var(--text-muted)' }}>{l.unit || ''}</td>}
                    {hasGranular && <td style={tdR}>{l.unit_cost_usd != null ? fmtUSD(l.unit_cost_usd) : '—'}</td>}
                    <td style={{ ...tdR, fontWeight: 600 }}>{fmtUSD(l.total_cost_usd)}</td>
                    <td style={{ ...tdR, color: '#10b981' }}>{l.federal_share_usd != null ? fmtUSD(l.federal_share_usd) : '—'}</td>
                    <td style={{ ...tdR, color: 'var(--text-muted)' }}>{l.match_share_usd != null ? fmtUSD(l.match_share_usd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const thL: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#111827' };
const thR: React.CSSProperties = { ...thL, textAlign: 'right' };
const td: React.CSSProperties = { padding: '6px 10px' };
const tdR: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
